import React, {useState} from 'react';
import {useRouter} from 'next/router';
import {sortBy} from 'lodash';
import moment from 'moment-timezone';
import {PlusIcon} from '@heroicons/react/solid';

import Link from '../../components/Link';
import ButtonNew, {ButtonTypes} from '../../components/ButtonNew';
import FacePile from '../../components/FacePile';
import CollapserThing from '../../components/CollapserThing';
import CompanyLogoPile from '../../components/CompanyLogoPile';
import VideoBadge from '../../components/VideoBadge';
import {
  CheckmarkIcon, BookmarkIcon, CaretIcon, ShortRightArrowIcon,
  Clock, SpeechBubbleIcon,
} from '../../components/svg';
import {useAnalytics} from '../../hooks';
import {cx, onPressEnter, clampLines} from '../../util';

export const MetaRow = ({question, query = {}, onClickSeen}) => {
  const {tracker} = useAnalytics();
  const {company: companyFilter} = query;

  const companies = companyFilter
    ? sortBy(question.companies || [], (c) => (c.id === companyFilter ? 0 : 1))
    : (question.companies || []);
  const logos = companies.filter((c) => c.imageUrl).slice(0, 3);
  const mostRecent = moment(question.last_asked_on ?? null);
  const answerCount = question.comments ?? question.commenters?.length;

  const handleClickSeen = (e) => {
    e.stopPropagation();
    tracker.questionListClickSeenQuestion({id: question.id, question: question.id});
    onClickSeen(question);
  };

  const items = [
    logos.length > 0 && {
      key: 'company',
      node: <CompanyLogoPile companies={companies} />,
    },
    mostRecent.isValid() && {
      key: 'date',
      node: (
        <>
          <Clock className="h-3.5 w-3.5 flex-shrink-0 text-gray-400" />
          {mostRecent.fromNow()}
        </>
      ),
    },
    answerCount > 0 && {
      key: 'answers',
      node: (
        <>
          <SpeechBubbleIcon stroke="currentColor" className="h-3.5 w-3.5 flex-shrink-0 text-gray-400" />
          {`${answerCount} answer${answerCount > 1 ? 's' : ''}`}
        </>
      ),
    },
    onClickSeen && {
      key: 'seen',
      node: (
        <button
          type="button"
          onClick={handleClickSeen}
          className="hidden md:inline-flex items-center gap-0.5 min-w-0 cursor-pointer"
        >
          <PlusIcon className="h-3.5 w-3.5 flex-shrink-0 text-gray-400" />
          <span className="truncate">I was asked this</span>
        </button>
      ),
    },
  ].filter(Boolean);

  return (
    <div className="flex items-center flex-wrap gap-x-3 gap-y-0.5 text-xs text-gray-500 mt-1">
      {items.map(({key, node}) => (
        <span key={key} className={cx('inline-flex items-center gap-1', key === 'seen' && 'min-w-0')}>
          {node}
        </span>
      ))}
    </div>
  );
};

const P = ({maxLines, className, children}) => (
  <p
    className={cx('relative text-sm text-gray-600', className)}
    style={clampLines(maxLines)}
  >
    {children}
  </p>
);

const ReadMoreLink = ({
  href, onClick, text = 'Read more', className,
}) => {
  const onClickLink = (e) => {
    e.stopPropagation();
    onClick();
  };

  return (
    <span className={cx('absolute right-0 bottom-0 bg-white', className)} style={{boxShadow: '-20px 0 9px 1px white'}}>
      <Link
        href={href}
        className="font-medium text-indigo hover:text-indigo focus:text-indigo"
        onClick={onClickLink}
      >
        {`${text} `}
        <ShortRightArrowIcon className="h-3 ml-0.5" />
      </Link>
    </span>
  );
};

export const AnswerPreview = ({question}) => {
  const [expanded, setExpanded] = useState(false);
  const {tracker} = useAnalytics();
  const panelId = `question-answer-preview-${question.id}`;

  const toggleExpanded = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setExpanded(!expanded);
    if (!expanded) tracker.questionListExpandAnswerPreview({id: question.id});
  };

  const clickReadMore = () => {
    tracker.questionListClickViewAnswers({question: question.id});
  };

  return question.commenters.length > 0 && (
    <div
      onClick={toggleExpanded}
      onKeyDown={onPressEnter(toggleExpanded)}
      tabIndex={0}
      role="button"
      className="w-full p-2.5 mt-3 border border-gray-200 rounded-lg text-sm font-normal text-left transition"
    >
      <div className="w-full flex justify-between">
        <div className="flex items-center min-w-0">
          <FacePile faces={question.commenters} small className="mr-5" />
          {expanded ? (
            <span className="font-medium">Community answers</span>
          ) : (
            <>
              <P maxLines={1} className="italic w-5/6 pr-1">
                {`"${question.topComment.comment}"`}
              </P>
            </>
          )}
        </div>
        <button
          type="button"
          className="whitespace-nowrap font-medium"
          onClick={toggleExpanded}
          aria-expanded={expanded}
          aria-controls={panelId}
          aria-label={expanded ? 'Collapse answer preview' : 'Expand answer preview'}
        >
          <span className="sr-only">
            {expanded ? 'Collapse answer preview' : 'Expand answer preview'}
          </span>
          <CaretIcon className={cx('ml-2.5 h-3 transform', {'rotate-90': expanded, '-rotate-90': !expanded})} />
        </button>
      </div>
      <CollapserThing visible={expanded}>
        <div id={panelId}>
          <P maxLines={3} className="italic mt-2 pr-1">
            {`${question.topComment.displayName} - "${question.topComment.comment}"`}
            <ReadMoreLink
              href={`/questions/${question.id}/${question.slug}?tab=community#answers`}
              onClick={clickReadMore}
              text="See full answer"
              className="not-italic"
            />
          </P>
        </div>
      </CollapserThing>
    </div>
  );
};

const ListItem = ({
  onSave,
  isGated,
  question,
  onClick,
  onClickPaywalledContent,
  onClickSeen,
  query,
}) => {
  const router = useRouter();
  const [saved, setSaved] = useState(Boolean(question.voted));
  const {practiced} = question;
  const href = `/questions/${question.id}/${question.slug}`;
  const hasVideo = Boolean(question.data?.video?.id);

  const handleClick = (e) => {
    if (isGated) {
      e.preventDefault();
      if (onClickPaywalledContent) onClickPaywalledContent(question.id);
    } else if (e.metaKey || e.ctrlKey) { // mimic CTRL+CLICK behavior
      window.open(href, '_blank');
    } else {
      router.push(href);
    }

    if (onClick) onClick(e, question);
  };

  const handleSave = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setSaved(!saved);
    onSave(question.id, !saved);
  };

  return (
    <li className="relative -mt-px first:mt-0 first:rounded-t-xl last:rounded-b-xl border border-gray-200 bg-white hover:bg-gray-50 transition-colors">
      <div
        onClick={handleClick}
        onKeyDown={onPressEnter(handleClick)}
        tabIndex={0}
        role="link"
        className="group flex items-center gap-4 px-4 py-4 cursor-pointer"
      >
        {practiced ? (
          <CheckmarkIcon className="w-6 h-6 -mx-0.5 flex-shrink-0 text-[rgb(16,185,129)]" />
        ) : (
          <span className="w-6 h-6 -mx-0.5 p-0.5 flex-shrink-0">
            <span className="block w-full h-full rounded-full border-2 border-gray-300" />
          </span>
        )}

        <div className="flex-1 min-w-0">
          <h3>
            <Link
              href={href}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleClick(e);
              }}
              className={cx(
                'text-base font-semibold leading-snug text-pretty',
                practiced ? 'text-gray-500 line-through' : 'text-gray-900',
              )}
            >
              {question.data.title}
              {hasVideo && (
                <span className="whitespace-nowrap">
                  {' '}
                  <VideoBadge className="ml-1 -mt-0.5 align-middle" />
                </span>
              )}
            </Link>
          </h3>
          <MetaRow question={question} query={query} onClickSeen={onClickSeen} />
        </div>

        <div className="hidden md:flex items-center gap-3 flex-shrink-0">
          <ButtonNew
            title={practiced ? 'Review' : 'Practice'}
            onClick={(e) => { e.stopPropagation(); handleClick(e); }}
            buttonType={practiced ? ButtonTypes.SECONDARY : ButtonTypes.SKEUOMORPHIC}
            className="inline-flex py-1 whitespace-nowrap cursor-pointer"
          >
            {practiced ? 'Review' : 'Practice'}
          </ButtonNew>

          <button
            type="button"
            title={saved ? 'Unsave question' : 'Save question'}
            onClick={handleSave}
            className={cx(
              'flex flex-shrink-0 cursor-pointer',
              saved ? 'text-yellow-400' : 'text-gray-300 hover:text-gray-400',
            )}
            aria-label={saved ? 'Unsave question' : 'Save question'}
          >
            <BookmarkIcon
              stroke="currentColor"
              fill={saved ? 'currentColor' : 'none'}
              strokeWidth={1.7}
              className="h-4 w-4"
            />
          </button>
        </div>
      </div>
    </li>
  );
};

export default ListItem;
